import { Component, Input, OnInit, Output, EventEmitter, ViewChild, OnChanges, ViewEncapsulation } from '@angular/core';
import { NgModel, ControlContainer, NgForm } from '@angular/forms';
import { TreeNode } from 'primeng/api';
import { StudyViewModel } from '../../viewmodel/study.viewmodel';
import { LookupService } from '@services/lookup.service';
import { CommonDataServicesService } from '@services/common-data-services.service';
import { TreeViewService } from '@services/treeview.service';
import { RegularExpressionPattern } from '../../shared/_constants';
import { FYAlignmentMethod, BasePLIVendorAccount } from '../_study_constants';
import { StudyType } from './study-type.model';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { OnDestroy } from '@angular/core';

@Component({
  selector: 'app-studyinfo',
  templateUrl: './studyInfo.component.html',
  styleUrls: ['./studyInfo.component.css'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  encapsulation: ViewEncapsulation.None,
})
export class StudyInfoComponent implements OnInit, OnDestroy, OnChanges {
  @Input() studyVM: StudyViewModel = new StudyViewModel();
  @Input() submitted = false;
  @Input() leveragedStudy = false;
  @Input() isAIEnableDisableToggleOnYes;
  @Output() currencyChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('engagementId', { static: false }) engagementIdInput: NgModel;

  regularExpressionPattern = RegularExpressionPattern;

  selectedBenchmarkCountryNodes: TreeNode[] = [];
  fyAlignmentMethod: typeof FYAlignmentMethod = FYAlignmentMethod;
  basePLIVendorAccount: typeof BasePLIVendorAccount = BasePLIVendorAccount;
  pliEligibleForDefaultVendorAccSetting: number[] = [];
  disableDefaultBasePLISetting = true;
  studyType = StudyType;
  private destroy$ = new Subject();

  constructor(
    private lookupService: LookupService,
    private treeviewService: TreeViewService,
    private commonDataServicesService: CommonDataServicesService,
  ) {}

  ngOnInit() {
    this.treeviewService.checkNode(this.studyVM.regionCountries_multi, this.studyVM.selectedBenchmarkCountries, this.selectedBenchmarkCountryNodes);
    this.lookupService.GetEligiblePLIForDefaultBaseCalculationSetting().subscribe((pliIds) => {
      this.pliEligibleForDefaultVendorAccSetting = pliIds;
      this.validatePLIBaseCalculationSettings();
    });
  }

  ngOnChanges(changes) {
    if (changes && changes['studyVM']) {
      this.treeviewService.checkNode(this.studyVM.regionCountries_multi, this.studyVM.selectedBenchmarkCountries, this.selectedBenchmarkCountryNodes);
      if (this.studyVM.study.isDemoStudy) {
        this.studyVM.study.engagementClientChannelName = 'Not Applicable';
      }
    }
    if (this.isAIEnableDisableToggleOnYes) this.studyVM.study.isAISuggestionsEnabled = this.isAIEnableDisableToggleOnYes;
  }

  showValidationMessage(item: NgModel): boolean {
    return item.errors && (this.submitted || !item.pristine || item.touched);
  }

  projectOfficeCountryChange() {
    this.studyVM.study.projectOfficeCityId = undefined;
    if (this.studyVM.study.projectOfficeCountryId) {
      this.lookupService
        .GetCitiesByCountry(this.studyVM.study.projectOfficeCountryId)
        .then((countryCities) => (this.studyVM.countryCities = countryCities));
    }
  }

  checkPeriodEnding() {
    return this.studyVM.checkPeriodEnding(this.studyVM.study.periodOfStudyFrom, this.studyVM.study.periodOfStudyTo);
  }

  checkPeriodTimeFrame() {
    return this.studyVM.checkPeriodTimeFrame(this.studyVM.study.periodOfStudyFrom, this.studyVM.study.periodOfStudyTo);
  }

  checkStudyDate() {
    return this.studyVM.checkStudyDate();
  }

  handleCurrencyChange() {
    this.currencyChange.emit(true);
  }

  validatePLIBaseCalculationSettings() {
    if (this.pliEligibleForDefaultVendorAccSetting.some((v) => this.studyVM.selectedRatios.indexOf(v) > -1)) {
      this.disableDefaultBasePLISetting = false;
    } else {
      this.disableDefaultBasePLISetting = true;
    }
  }

  validateEngagementId() {
    if (this.engagementIdInput.value) {
      if (!this.studyVM.study.isDemoStudy) {
        if (this.engagementIdInput.value.length >= 8 && this.engagementIdInput.value.length < 30) {
          this.commonDataServicesService
            .getEngagementClient(this.engagementIdInput.value)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (response) => {
                if (response) {
                  this.engagementIdInput.control.setErrors(null);
                  this.studyVM.study.engagementStatusCode = response.engagement.engagementStatusCode;
                  this.studyVM.study.engagementClientId = response.engagement.engagementClientId;
                  this.studyVM.study.engagementOpportunityId = response.engagement.engagementOpportunityId;
                  this.studyVM.study.engagementSubServiceLine = response.engagement.engagementSubServiceLine;
                  this.studyVM.study.engagementClientChannelName =
                    response.client.clientChannelName == null ? ' ' : response.client.clientChannelName;
                } else {
                  this.clearEngagementModel();
                  this.engagementIdInput.control.setErrors({ invalidNumber: true });
                }
              },
              error: () => {
                this.clearEngagementModel();
                this.engagementIdInput.control.setErrors({ invalidNumber: true });
              },
            });
        } else {
          this.engagementIdInput.control.setErrors({ minlength: true });
        }
      } else {
        this.engagementIdInput.control.setErrors(null);
      }
    } else {
      this.engagementIdInput.control.setErrors({ required: true });
    }
    this.engagementIdInput.control.markAsTouched();
  }

  clearEngagementModel() {
    this.studyVM.study.engagementStatusCode = null;
    this.studyVM.study.engagementClientId = null;
    this.studyVM.study.engagementOpportunityId = null;
    this.studyVM.study.engagementSubServiceLine = null;
    this.studyVM.study.engagementClientChannelName = ' ';
  }

  onDemoStudyTypeChanged() {
    this.studyVM.study.studyTypeId = this.studyVM.study.isDemoStudy ? this.studyType.Test : this.studyType.NonStandard;
    this.studyVM.study.engagementClientChannelName = this.studyVM.study.isDemoStudy ? 'Not Applicable' : ' ';
    this.validateEngagementId();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }
}
